import {inject, Injectable} from './core/di';
import {ConfigLoaderService} from './core/config/config-loader-service';
import {LegacyCustomCssLoaderService} from './core/legacy-custom-css-loader-service';
import {ScreenSizeService} from './core/screen-size-service';
import {UrlsService} from './core/urls-service';
import {HotelInfoService} from './core/hotel-info-service';
import {Logger} from './core/logger';
import {AppInitializeService} from './core/app-initialize-service';
import {TvVendorService} from './core/vendor/tv-vendor-service';
import {RoomNumberService} from './core/room-number-service';
import {NetworkInfoService} from './core/network-info-service';
import {TimeService} from './core/time-service';
import {RemoteDebugService} from './core/remote-debug-service';
import {NavigationService} from './core/navigation-service';
import {GuestService} from './core/guest-service';
import {ChannelService} from './core/channel-service';
import {BootstrapService} from './core/bootstrap-service';
import {GlobalErrorHandlerService} from './core/global-error-handler-service';
import {version} from './core/utils/version';
import {TemplatesService} from './core/view-engine/templates-service';
import {ComponentsService} from './core/view-engine/components-service';
import {I18nService} from './core/i18n/i18n-service';
import {StructService} from './core/struct/struct-service';
import {MomentLocaleService} from './core/i18n/moment-locale-service';
import {LocalizedStylesService} from './core/i18n/localized-styles-service';
import {FontPreloadingService} from './core/font-preloading-service';
import {MediaService} from './core/media-service';
import {LoaderService} from './core/loader/loader-service';
import {UiService} from './core/view-engine/ui-service';
import {CustomizationService} from './core/customization-service';
import {TouchInterfaceService} from './core/touch-interface-service';
import {ProgressBarService} from './core/progress-bar/progress-bar-service';
import {LegacyPersistentLogService} from './core/logger/legacy-persistent-log-service';
import {LegacyLogService} from './core/logger/legacy-log-service';
import {TvModuleService} from './core/modules/tv-module-service';
import {ServiceCodesService} from './core/service-codes-service';
import {EventsService} from './core/events-service';
import {UtilsService} from './core/utils/utils-service';
import {ImagePuncherService} from './core/image-puncher-service';
import {WidgetService} from './core/widget-service';

@Injectable()
export class App {
  private readonly logger = inject(Logger);

  constructor() {
    setTimeout(() => {
      if (document.readyState === 'complete') {
        this.logger.debug('[App] Document ready state: ' + document.readyState);
        this.start();
      } else {
        // NOTE: We specifically wait for the `load` event and only then start initializing services. This way we don't
        // block the initial parsing and rendering, allowing the loading screen to be drawn without lags.
        window.addEventListener(
          'load',
          () => {
            this.logger.debug('[App] DOMContentLoaded event fired.');
            this.start();
          },
          false,
        );
      }
    }, 0);
  }

  private start() {
    this.init().then(
      () => {
        // this.logger.log('[App] Application started successfully');
        // todo uncomment after bootstrap migration
      },
      (err) => {
        this.logger.critical('[App] Application failed to start', err);
      },
    );
  }

  private async init() {
    // NOTE: This is the place where you can initialize all the necessary things in the right order. During the
    // initialization, the user will see the loading screen, and we can calmly download everything that is needed,
    // initialize, prepare, render to the DOM, and only then show it to the user.

    try {
      this.logger.debug(`[APP] Application start`);

      this.logger.log(`[APP] version: hoteza.tv@${version.semver} (${version.date})`);
      this.logger.log(`[APP] Local startup time: ${new Date().toISOString()}`);
      const hotelInfoService = inject(HotelInfoService);
      this.logger.log('[App] Region: ' + hotelInfoService.region);
      this.logger.log('[App] Hotel ID: ' + hotelInfoService.hotelId);
      this.logger.log('[App] Hotel UID: ' + hotelInfoService.hotelUid);

      this.logger.debug(`[APP] Initializing services...`);

      // Define global namespace TODO: refactor all global variables to hoteza namespace
      window.hoteza = window.hoteza || {};

      /**
       * A list of services that need to be asynchronously initialized in a specific order before the application
       * officially starts.
       */
      const servicesToInit: Array<[new (...args: any[]) => AppInitializeService | object, string]> = [
        [GlobalErrorHandlerService, 'GlobalErrorHandlerService'],
        [BootstrapService, 'BootstrapService'],
        [ConfigLoaderService, 'ConfigLoaderService'],
        [ScreenSizeService, 'ScreenSizeService'],
        [UrlsService, 'UrlsService'],
        [TvVendorService, 'TvVendorService'],
        [NetworkInfoService, 'NetworkInfoService'],
        [RoomNumberService, 'RoomNumberService'],
        [TimeService, 'TimeService'],
        [LegacyPersistentLogService, 'LegacyPersistentLogService'],
        [LegacyLogService, 'LegacyLogService'],
        [ServiceCodesService, 'ServiceCodesService'],
        [RemoteDebugService, 'RemoteDebugService'],
        [NavigationService, 'NavigationService'],
        [TemplatesService, 'TemplatesService'],
        [ComponentsService, 'ComponentsService'],
        [StructService, 'StructService'],
        [I18nService, 'I18nService'],
        [MomentLocaleService, 'MomentLocaleService'],
        [LocalizedStylesService, 'LocalizedStylesService'],
        [GuestService, 'GuestService'],
        [ChannelService, 'ChannelService'],
        [TouchInterfaceService, 'TouchInterfaceService'],
        [FontPreloadingService, 'FontPreloadingService'],
        [LoaderService, 'LoaderService'],
        [MediaService, 'MediaService'],
        [UiService, 'UiService'],
        [ProgressBarService, 'ProgressBarService'],
        [EventsService, 'EventsService'],
        [UtilsService, 'UtilsService'],
        [ImagePuncherService, 'ImagePuncherService'],

        [LegacyCustomCssLoaderService, 'LegacyCustomCssLoaderService'],
        [CustomizationService, 'CustomizationService'],
        [WidgetService, 'WidgetService'],
        [TvModuleService, 'TvModuleService'],
        // todo грузить некоторые модули параллельно, например кастомизацию и модули
      ];

      for (let i = 0; i < servicesToInit.length; i++) {
        const [serviceType, serviceName] = servicesToInit[i];
        try {
          this.logger.debug(`[APP] Initializing ${serviceName} service...`);
          const service = inject(serviceType);

          const serviceWithAsyncInitialization = service as AppInitializeService;
          if (typeof serviceWithAsyncInitialization.init === 'function') {
            const initializationSuccessful = await serviceWithAsyncInitialization.init();

            if (!initializationSuccessful) {
              this.logger.warn(`[App] ${serviceName} service initialization failed`);
              // No additional action required. Bootstrap service will reload the app.
              return;
            }
          }
        } catch (err) {
          this.logger.critical(`[APP] ${serviceName} service initialization failed`, err);
          return;
        }
      }

      this.logger.debug(`[APP] Services initialization completed successfully`);

      this.initLegacy();

      this.logger.debug(`[APP] Application initialization finished`);

      // mark app as loaded
      inject(BootstrapService).setAppLoaded();
    } catch (err) {
      this.logger.critical(`[APP] Application initialization failed`, err);
    }
  }

  /**
   * Ex function fs_read()
   */
  private initLegacy() {
    // TODO refactor to modules

    //TODO: перенести в модули
    // если получаем массив, значит поле пустое
    // в противном случае здесь будет объект
    // if (!$.isArray(structv2.yandex_station)) {
    //   YandexStation.init();
    // }
    //
    // Promise.allSettled([Modules.load_all()]).then(function () {
    //
    // });

    window.document_ready();
  }
}
